/**
 * Infraction Modal Component
 * 
 * Handles the UI for warning, muting, and banning users.
 * Refactored from moderation.js for shared use between Admin and Frontend.
 * 
 * @package PresszoneComments
 */

import { el, __ } from '../utils/dom.js';
import API from '../utils/api.js';
import Modal from './Modal.js';
import { Button, CancelButton } from './Button.js';

/**
 * Get human-readable label for suggested action.
 */
export function getSuggestedActionLabel(suggested) {
    const typeLabels = {
        warning: __('Warning', 'comments-press-zone'),
        mute: __('Mute', 'comments-press-zone'),
        ban: __('Ban', 'comments-press-zone')
    };

    let label = typeLabels[suggested.type] || suggested.type;

    if (suggested.duration) {
        const duration = suggested.duration;
        if (duration >= 86400 * 7) {
            label += ` (${Math.round(duration / 86400 / 7)} ${__('week(s)', 'comments-press-zone')})`;
        } else if (duration >= 86400) {
            label += ` (${Math.round(duration / 86400)} ${__('day(s)', 'comments-press-zone')})`;
        } else if (duration >= 3600) {
            label += ` (${Math.round(duration / 3600)} ${__('hour(s)', 'comments-press-zone')})`;
        }
    } else if (suggested.type === 'ban' && !suggested.duration) {
        label += ' (' + __('Permanent', 'comments-press-zone') + ')';
    }

    return label;
}

export async function showInfractionModal(userId, resultsContainer, options = {}) {
    const {
        title = __('Penalty', 'comments-press-zone'),
        initialType = 'warning',
        suggested = {}
    } = options;

    // Dynamic import to avoid circular dependencies if this file is imported early
    const { default: UserAutocomplete } = await import('./UserAutocomplete.js');

    let selectedUserId = userId || null;

    // Determine pre-selected values from suggested action or options
    const preselectedType = suggested.type || initialType || 'warning';
    const preselectedDuration = suggested.duration || null;
    const showDuration = preselectedType === 'mute' || preselectedType === 'ban';

    const modal = new Modal({
        title: title,
        width: '540px'
    });

    const form = el('form', {
        class: 'presszone-comments-form',
        style: 'min-height: 350px;',
        onsubmit: (e) => handleWarningSubmit(e, () => selectedUserId, modal, resultsContainer)
    },
        // User Search (only if no userId provided)
        !userId ? el('div', { class: 'presszone-comments-form-group' },
            el('label', { for: 'infraction-user-search' }, __('User', 'comments-press-zone') + ' *'),
            UserAutocomplete({
                id: 'infraction-user-search',
                placeholder: __('Search user by name or email...', 'comments-press-zone'),
                inputProps: { required: true },
                onSelect: (user) => { selectedUserId = user.id; }
            })
        ) : null,

        el('div', { class: 'presszone-comments-form-group' },
            el('label', { for: 'warning-type' }, __('Type', 'comments-press-zone')),
            el('select', {
                id: 'warning-type',
                name: 'type',
                required: true,
                class: 'presszone-comments-select--settings',
                onchange: handleWarningTypeChange
            },
                el('option', { value: 'warning', selected: preselectedType === 'warning' }, __('Warning (note only)', 'comments-press-zone')),
                el('option', { value: 'mute', selected: preselectedType === 'mute' }, __('Mute (cannot comment)', 'comments-press-zone')),
                el('option', { value: 'ban', selected: preselectedType === 'ban' }, __('Ban', 'comments-press-zone'))
            )
        ),
        el('div', {
            class: 'presszone-comments-form-group',
            id: 'warning-duration-group',
            style: showDuration ? 'display: block;' : 'display: none;'
        },
            el('label', { for: 'warning-duration' }, __('Duration', 'comments-press-zone')),
            el('select', { id: 'warning-duration', name: 'duration', class: 'presszone-comments-select--settings' },
                el('option', { value: '3600', selected: preselectedDuration === 3600 }, __('1 Hour', 'comments-press-zone')),
                el('option', { value: '86400', selected: preselectedDuration === 86400 || (!preselectedDuration && preselectedType === 'ban' && initialType === 'ban') }, __('24 Hours', 'comments-press-zone')), // Default for ban
                el('option', { value: '604800', selected: preselectedDuration === 604800 || (preselectedDuration && preselectedDuration < 2592000 && preselectedDuration > 86400) }, __('1 Week', 'comments-press-zone')),
                el('option', { value: '2592000', selected: preselectedDuration && preselectedDuration >= 2592000 && preselectedDuration < 2592000 * 6 }, __('1 Month', 'comments-press-zone')),
                el('option', { value: 'permanent', selected: preselectedDuration === null && preselectedType === 'ban' && initialType !== 'ban' }, __('Permanent', 'comments-press-zone'))
            )
        ),
        el('div', { class: 'presszone-comments-form-group' },
            el('label', { for: 'warning-reason', style: 'display: flex; align-items: center; gap: 6px;' },
                __('Reason', 'comments-press-zone') + ' *',
                el('span', {
                    class: 'presszone-comments-tooltip',
                    title: __('This message will be sent to the user via email and shown when they view their warnings.', 'comments-press-zone'),
                    style: 'cursor: help; font-size: 14px;'
                }, 'ℹ️')
            ),
            el('textarea', {
                id: 'warning-reason',
                name: 'reason',
                rows: 3,
                required: true,
                class: 'presszone-comments-textarea',
                placeholder: __('Describe the reason for this action...', 'comments-press-zone')
            })
        ),
        // Collapsible moderator notes section
        el('details', { class: 'presszone-comments-details', style: 'margin-bottom: 16px;' },
            el('summary', {
                style: 'cursor: pointer; color: var(--presszone-comments-text-muted); font-size: 13px; padding: 8px 0; display: flex; align-items: center; gap: 6px;',
                title: __('Private notes for moderators and admins only. The user will never see this.', 'comments-press-zone')
            },
                '📝 ' + __('Add Moderator Notes (internal)', 'comments-press-zone'),
                el('span', { style: 'opacity: 0.6; font-size: 12px;' }, '🔒')
            ),
            el('div', { class: 'presszone-comments-form-group', style: 'margin-top: 12px;' },
                el('textarea', {
                    id: 'warning-notes',
                    name: 'moderator_notes',
                    rows: 2,
                    required: false,
                    class: 'presszone-comments-textarea',
                    placeholder: __('Internal notes visible only to moderators...', 'comments-press-zone'),
                    style: 'font-size: 13px;'
                })
            )
        )
    );

    // Suggested action banner
    const topContent = suggested.type ? el('div', {
        class: 'presszone-comments-modal__suggestion',
        style: 'padding: 12px 16px; background: rgba(245, 158, 11, 0.1); border-radius: 6px; margin-bottom: 16px; display: flex; align-items: center; gap: 8px;'
    },
        el('span', {}, '💡'),
        el('span', { style: 'font-size: 13px;' },
            el('strong', {}, __('Suggested:', 'comments-press-zone') + ' '),
            getSuggestedActionLabel(suggested)
        )
    ) : null;

    const content = el('div', {}, topContent, form);

    const actions = [
        Button({
            label: __('OK', 'comments-press-zone'),
            variant: initialType === 'ban' ? 'danger' : 'warning',
            onClick: () => {
                // Use requestSubmit for proper form validation and submission
                if (form.requestSubmit) {
                    form.requestSubmit();
                } else {
                    // Fallback for older browsers
                    form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }
            }
        }),
        CancelButton(() => modal.close())
    ];

    modal.render(content, actions);
    modal.open();
}

function handleWarningTypeChange(e) {
    const durationGroup = document.getElementById('warning-duration-group');
    if (e.target.value === 'mute' || e.target.value === 'ban') {
        durationGroup.style.display = 'block';
    } else {
        durationGroup.style.display = 'none';
    }
}

async function handleWarningSubmit(e, getUserId, modal, resultsContainer) {
    e.preventDefault();
    const form = e.target;

    // Validate form
    if (!form.checkValidity()) {
        form.reportValidity();
        return;
    }

    const userId = getUserId();
    if (!userId) {
        if (typeof window.addNotice === 'function') {
            window.addNotice(__('Please select a user.', 'comments-press-zone'), 'error');
        } else {
            const errorMsg = el('div', { class: 'presszone-comments-error-msg', style: 'color: var(--presszone-comments-error); margin-bottom: 8px;' }, __('Please select a user.', 'comments-press-zone'));
            form.prepend(errorMsg);
            setTimeout(() => errorMsg.remove(), 3000);
        }
        return;
    }

    const type = form.type.value;
    const reason = form.reason.value.trim();
    const durationVal = form.duration?.value || null;
    const moderatorNotes = form.moderator_notes?.value?.trim() || null;

    let duration = null;
    if (durationVal && durationVal !== 'permanent') {
        duration = parseInt(durationVal, 10);
    } else if (durationVal === 'permanent') {
        duration = null;
    }

    try {
        const result = await API.post(`/users/${userId}/warnings`, {
            type,
            reason,
            duration,
            moderator_notes: moderatorNotes
        });

        modal.close();

        // Show success notification
        if (typeof window.addNotice === 'function') {
            window.addNotice(result.message || __('Action completed successfully.', 'comments-press-zone'), 'success');
        }

        // Dispatch event for UI refreshes (Decoupled from specific pages)
        window.dispatchEvent(new CustomEvent('presszone-infraction-success', {
            detail: {
                userId,
                type,
                resultsContainer
            }
        }));

    } catch (error) {
        if (typeof window.addNotice === 'function') {
            window.addNotice(__('Failed to issue infraction.', 'comments-press-zone') + ' ' + (error.message || ''), 'error');
        }
        console.error('Error issuing infraction:', error);
    }
}

export default showInfractionModal;
